/**
 * Excel Template Generator for Roster Bulk Import
 * Creates a downloadable Excel template with proper headers and sample data
 */

import { detectAllDuplicates, getUniqueMembers } from './duplicateDetection';

/**
 * Generate and download Excel template for roster import
 */
export const downloadRosterTemplate = () => {
  // Define the template structure
  const headers = [
    'First Name*',
    'Last Name*', 
    'Date of Birth (MM-DD-YYYY or MM/DD/YYYY)*',
    'Email (Optional)'
  ];

  // Sample data to help users understand the format
  const sampleData = [
    ['John', 'Doe', '03-15-2005', 'john.doe@email.com'],
    ['', '', '', 'DELETE THIS SAMPLE ROW BEFORE IMPORTING']
  ];

  // Create CSV content (Excel will open CSV files)
  const csvContent = [
    headers.join(','),
    ...sampleData.map(row => 
      row.map(cell => {
        // Escape commas and quotes in cell content
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    )
  ].join('\n');

  // Create and download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'roster_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse uploaded CSV/Excel file for roster import
 * @param {File} file - The uploaded file
 * @returns {Promise<Object>} - Parsed data with validation results
 */
export const parseRosterFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const lines = content.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          reject(new Error('File is empty'));
          return;
        }

        // Parse header row
        const headers = parseCSVRow(lines[0]);
        const expectedHeaders = [
          'first name',
          'last name', 
          'date of birth',
          'email'
        ];

        // Validate headers (case-insensitive, ignore asterisks and extra text)
        const normalizedHeaders = headers.map(h => 
          h.toLowerCase().replace(/\*/g, '').replace(/\s*\([^)]*\)/g, '').trim()
        );

        const headerMapping = {};
        const missingHeaders = [];

        expectedHeaders.forEach((expected, index) => {
          const foundIndex = normalizedHeaders.findIndex(h => 
            h.includes(expected) || expected.includes(h)
          );
          
          if (foundIndex !== -1) {
            headerMapping[expected] = foundIndex;
          } else {
            missingHeaders.push(expected);
          }
        });

        if (missingHeaders.length > 0) {
          reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
          return;
        }

        // Check for 250 user limit before processing
        const dataRowCount = lines.length - 1; // Subtract header row
        if (dataRowCount > 250) {
          reject(new Error(`Too many users in file. Maximum allowed: 250, found: ${dataRowCount}. Please split your import into smaller batches.`));
          return;
        }

        // Parse data rows
        const parsedData = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const row = parseCSVRow(lines[i]);
          const rowData = {};
          
          // Skip empty rows
          if (row.every(cell => !cell.trim())) continue;
          
          // Map data using header positions
          Object.entries(headerMapping).forEach(([field, position]) => {
            rowData[field] = row[position] ? row[position].trim() : '';
          });

          // Validate required fields
          const rowErrors = [];
          if (!rowData['first name']) rowErrors.push('First Name is required');
          if (!rowData['last name']) rowErrors.push('Last Name is required');
          
          // Validate date format - required for dancers
          if (!rowData['date of birth']) {
            rowErrors.push('Date of Birth is required');
          } else if (!isValidDate(rowData['date of birth'])) {
            rowErrors.push('Invalid date format. Please use MM-DD-YYYY or MM/DD/YYYY format (e.g., 03-15-2005 or 03/15/2005)');
          }

          // Validate email format if provided
          if (rowData['email'] && !isValidEmail(rowData['email'])) {
            rowErrors.push('Invalid email format');
          }

          // Format the data for our system - automatically assign Dancer role
          const memberData = {
            firstName: rowData['first name'],
            lastName: rowData['last name'],
            email: rowData['email'] || '',
            dateOfBirth: rowData['date of birth'] ? convertToBackendDateFormat(rowData['date of birth']) : '',
            gender: '', // Gender will be assigned in the next step
            role: ['Dancer'], // Automatically assign Dancer role for bulk import
            rowNumber: i + 1,
            errors: rowErrors
          };

          parsedData.push(memberData);
          
          if (rowErrors.length > 0) {
            errors.push({
              row: i + 1,
              errors: rowErrors,
              data: memberData
            });
          }
        }

        resolve({
          success: true,
          data: parsedData,
          errors: errors,
          totalRows: parsedData.length,
          validRows: parsedData.filter(row => row.errors.length === 0).length,
          invalidRows: errors.length
        });

      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Parse a CSV row handling quoted values and commas
 */
const parseCSVRow = (row) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    
    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i++; // skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
};

/**
 * Validate email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate date format (MM-DD-YYYY or MM/DD/YYYY) and convert to YYYY-MM-DD
 */
const isValidDate = (dateString) => {
  // Check for MM-DD-YYYY or MM/DD/YYYY format
  const dateRegex = /^\d{2}[-/]\d{2}[-/]\d{4}$/;
  if (!dateRegex.test(dateString)) {
    return false;
  }
  
  // Parse the MM-DD-YYYY or MM/DD/YYYY format
  const parts = dateString.split(/[-/]/);
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  
  // Validate ranges
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
    return false;
  }
  
  // Create date and validate it's real
  const date = new Date(year, month - 1, day);
  
  // Ensure the date parts match what was input (catches invalid dates like 02-30-2023)
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
};

/**
 * Convert MM-DD-YYYY or MM/DD/YYYY to YYYY-MM-DD format for backend
 */
const convertToBackendDateFormat = (dateString) => {
  if (!dateString || !isValidDate(dateString)) {
    return dateString;
  }
  
  const parts = dateString.split(/[-/]/);
  const month = parts[0];
  const day = parts[1];
  const year = parts[2];
  
  return `${year}-${month}-${day}`;
};

/**
 * Convert YYYY-MM-DD to MM-DD-YYYY format for display
 */
export const convertToDisplayDateFormat = (dateString) => {
  if (!dateString) return '';
  
  // If already in MM-DD-YYYY format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    return dateString;
  }
  
  // If in YYYY-MM-DD format, convert to MM-DD-YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
  }
  
  return dateString;
};

/**
 * Format gender to proper case
 */
const formatGender = (gender) => {
  const formatted = gender.toLowerCase();
  if (formatted === 'male') return 'Male';
  if (formatted === 'female') return 'Female';
  if (formatted === 'non-binary') return 'Non-binary';
  return gender; // return original if not recognized
};

/**
 * Generate preview data for display with duplicate detection
 */
export const generatePreviewData = (parsedData, existingRoster = []) => {
  const validMembers = parsedData.data.filter(member => member.errors.length === 0);
  const invalidMembers = parsedData.data.filter(member => member.errors.length > 0);
  
  // Detect duplicates
  const duplicateData = detectAllDuplicates(validMembers, existingRoster);
  
  return {
    summary: {
      totalRows: parsedData.data.length,
      validRows: parsedData.validRows,
      invalidRows: parsedData.invalidRows,
      hasErrors: parsedData.errors.length > 0,
      hasDuplicates: duplicateData.hasAnyDuplicates,
      totalDuplicates: duplicateData.totalDuplicates
    },
    validMembers,
    invalidMembers,
    allErrors: parsedData.errors,
    duplicates: duplicateData,
    uniqueMembers: getUniqueMembers(validMembers, duplicateData.internal)
  };
};

/**
 * Parse roster file with comprehensive validation and duplicate detection
 */
export const parseRosterFileWithDuplicates = async (file, existingRoster = []) => {
  try {
    const parsedData = await parseRosterFile(file);
    const previewData = generatePreviewData(parsedData, existingRoster);
    
    return {
      ...parsedData,
      preview: previewData
    };
  } catch (error) {
    throw error;
  }
};
